/**
 * Software digital zoom: a second, throttled decode pass over the centre of the
 * live camera frame.
 *
 * Some budget Android rear cameras cannot focus inside the distance that would
 * make a product barcode fill the frame, and expose no zoom range to compensate
 * with. Held back far enough to be sharp, the bars are simply too small for the
 * full-frame decoder. This loop reads the same <video> the main scanner owns,
 * crops what the operator framed, draws it into one reused canvas and decodes
 * that.
 *
 * It never touches the camera: the main scanner keeps owning start/stop, and a
 * hit here is routed through the caller's normal dedup + record path. A bad
 * frame costs only that frame; a pass that fails every frame reports once and
 * stops. What the caller does with a decoded code is the caller's business —
 * those failures are never swallowed here.
 */
import { computeAimRoiCrop, computeRoiCrop } from './roi-crop'
import { sharpenLuminance, toLuminance } from './frame-luminance'
import { createZxingLuminanceDecoder } from './zxing-luminance-decoder'
import type { DecodeLuminance } from './zxing-luminance-decoder'

/** Rendered size of the preview stage and of the aiming frame inside it. */
export interface AimMeasurement {
    stageW: number
    stageH: number
    aimW: number
    aimH: number
}

export interface SoftwareZoomScanOptions {
    /** The live preview element owned by the main scanner. */
    video: HTMLVideoElement
    /** Called with each decoded text; the caller applies dedup and recording. */
    onDecode: (text: string) => void
    /**
     * Current layout of the aiming frame, read per tick so rotation and
     * resizing stay correct. Returning null falls back to the fixed fraction.
     */
    measureAim?: () => AimMeasurement | null
    /**
     * Called once when the pass has failed on every frame for long enough to
     * be broken rather than unlucky; the loop stops itself first.
     */
    onFatal?: (error: unknown) => void
    /** Decoder seam — defaults to ZXing. */
    decode?: DecodeLuminance
    intervalMs?: number
    fallbackRoiScale?: number
    maxUpscale?: number
}

/**
 * Cadence and crop size are the CPU budget: this loop runs next to the
 * full-frame decoder and a weak phone carries both. The fallback fraction
 * approximates the aiming frame on a 16:9 feed inside the 3:4 preview, and is
 * only used when the layout cannot be measured.
 */
const DEFAULT_INTERVAL_MS = 300
const DEFAULT_FALLBACK_ROI_SCALE = 0.35
const DEFAULT_MAX_UPSCALE = 2

const HAVE_CURRENT_DATA = 2
/**
 * A frame can fail for reasons that pass (the video between two streams, a
 * transient allocation). Failing this many times in a row is a broken pass:
 * report it and stop burning CPU on it.
 */
const MAX_CONSECUTIVE_FAILURES = 5

/** Buffers sized once per canvas size and reused across frames. */
class FrameBuffers {
    private grey = new Uint8ClampedArray(0)
    private sharp = new Uint8ClampedArray(0)

    take(pixels: number): {
        grey: Uint8ClampedArray
        sharp: Uint8ClampedArray
    } {
        if (this.grey.length !== pixels) {
            this.grey = new Uint8ClampedArray(pixels)
            this.sharp = new Uint8ClampedArray(pixels)
        }
        return { grey: this.grey, sharp: this.sharp }
    }
}

/**
 * Start the loop. Returns a stop function; call it when the camera stops or the
 * dialog closes. Safe to call stop more than once.
 */
export function startSoftwareZoomScan(
    opts: SoftwareZoomScanOptions,
): () => void {
    const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS
    const fallbackRoiScale = opts.fallbackRoiScale ?? DEFAULT_FALLBACK_ROI_SCALE
    const maxUpscale = opts.maxUpscale ?? DEFAULT_MAX_UPSCALE
    const decode = opts.decode ?? createZxingLuminanceDecoder()

    const canvas = document.createElement('canvas')
    // willReadFrequently: every frame is read back with getImageData.
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    const buffers = new FrameBuffers()

    let stopped = false
    let timer: ReturnType<typeof setTimeout> | null = null

    let consecutiveFailures = 0

    /** Returns the decoded text, or null when this frame had nothing to give. */
    const scanFrame = (): string | null => {
        const { video } = opts
        if (!ctx) return null
        // A backgrounded tab paints no new frames; decoding the last one again
        // only costs battery.
        if (document.hidden) return null
        if (video.readyState < HAVE_CURRENT_DATA) return null
        const { videoWidth, videoHeight } = video
        if (videoWidth <= 0 || videoHeight <= 0) return null

        const measured = opts.measureAim?.() ?? null
        const crop =
            (measured &&
                computeAimRoiCrop({
                    ...measured,
                    videoW: videoWidth,
                    videoH: videoHeight,
                    maxUpscale,
                })) ||
            computeRoiCrop(
                videoWidth,
                videoHeight,
                fallbackRoiScale,
                maxUpscale,
            )
        if (canvas.width !== crop.dWidth || canvas.height !== crop.dHeight) {
            canvas.width = crop.dWidth
            canvas.height = crop.dHeight
        }
        ctx.drawImage(
            video,
            crop.sx,
            crop.sy,
            crop.sWidth,
            crop.sHeight,
            0,
            0,
            crop.dWidth,
            crop.dHeight,
        )

        const frame = ctx.getImageData(0, 0, crop.dWidth, crop.dHeight)
        const { grey, sharp } = buffers.take(crop.dWidth * crop.dHeight)
        toLuminance(frame.data, grey)
        const pixels = sharpenLuminance(grey, sharp, crop.dWidth, crop.dHeight)

        return decode(pixels, crop.dWidth, crop.dHeight)
    }

    const tick = (): void => {
        if (stopped) return
        let text: string | null = null
        try {
            text = scanFrame()
            consecutiveFailures = 0
        } catch (error) {
            // One bad frame must only cost this pass; the main scanner keeps
            // running either way.
            consecutiveFailures += 1
            if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                stopped = true
                opts.onFatal?.(error)
                return
            }
        }
        // Schedule before handing the text over: recording is the caller's
        // code, and its failures belong to the caller, not to this loop — they
        // must neither be swallowed here nor kill the scanning pass.
        if (!stopped) timer = setTimeout(tick, intervalMs)
        if (text) opts.onDecode(text)
    }

    // No 2D context (headless/jsdom, or a browser refusing the canvas) means no
    // software zoom on this device — degrade to the full-frame scanner alone.
    if (ctx) timer = setTimeout(tick, intervalMs)

    return () => {
        stopped = true
        if (timer) {
            clearTimeout(timer)
            timer = null
        }
    }
}
